import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { DRIVER_REG_STEPS, USER_ROLES, USER_STATUS } from '../../config/constants';
import {
  validateInvitationLink, markInvitationUsed, saveApplicationProgress,
  uploadDocument, uploadVehiclePhoto, verifyABN, saveVehicleDetails,
  submitApplication, createStripeConnectAccount, checkPhoneAvailable,
} from '../../services/driverService';
import { recordDriverConsent } from '../../services/complianceService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import VEHICLE_MAKES from '../../config/vehicleMakes';
import { classifyVehicleTier, TIER_INFO } from '../../config/vehicleTiers';
import NSW_SUBURBS from '../../config/nswSuburbs';
import NSWRegionMap from '../../components/maps/NSWRegionMap';

// ═══════════════ NSW SUBURBS LIST ═══════════════


// ═══════════════ VALIDATION HELPERS ═══════════════
const today = () => new Date().toISOString().split('T')[0];

function isExpired(dateStr) {
  if (!dateStr) return true;
  return new Date(dateStr) < new Date();
}

function isDateTooSoon(dateStr, minDaysAhead = 30) {
  if (!dateStr) return true;
  const expiry = new Date(dateStr);
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + minDaysAhead);
  return expiry < minDate;
}

// True if a past date (e.g. police-check issue date) is older than maxDaysAgo.
// ─── Document upload validation (mirror of server allowlist in driverService) ───
const ALLOWED_DOC_EXT = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
const ALLOWED_DOC_MIME = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_DOC_MB = 10;
function validateUploadFile(file) {
  if (!file) return { ok: false, reason: 'No file selected' };
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const mimeOk = file.type ? ALLOWED_DOC_MIME.includes(file.type.toLowerCase()) : false;
  const extOk = ALLOWED_DOC_EXT.includes(ext);
  if (!mimeOk && !extOk) return { ok: false, reason: 'Unsupported file type. Use JPEG, PNG, WebP, or PDF.' };
  if (file.size > MAX_DOC_MB * 1024 * 1024) return { ok: false, reason: `File too large (max ${MAX_DOC_MB}MB).` };
  return { ok: true };
}

// Maps each document field to the wizard step it lives on (for jump-to-error).
const DOC_FIELD_STEP = { licenceFront: 2, licenceBack: 2, proofOfIdentity: 2, policeCheck: 2, driverAuthority: 3, wwccDocument: 4, ctpInsurance: 7, comprehensiveInsurance: 7, vehicleRego: 7 };

function isDateTooOld(dateStr, maxDaysAgo = 90) {
  if (!dateStr) return true;
  const d = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - maxDaysAgo);
  return d < cutoff;
}

// ═══════════════ STEP 1: PERSONAL DETAILS ═══════════════
function StepPersonalDetails({ data, onChange, errors }) {
  const [showPw, setShowPw] = useState(false);
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Personal Details</h2>
      <p className="text-body mb-8 leading-relaxed">Basic information about you.</p>
      <div className="flex gap-4">
        <div className="form-group flex-1">
          <label className="form-label">First name *</label>
          <input className={`form-input ${errors.firstName ? 'error' : ''}`} value={data.firstName || ''} onChange={(e) => onChange('firstName', e.target.value)} placeholder="John" />
          {errors.firstName && <div className="form-error">{errors.firstName}</div>}
        </div>
        <div className="form-group flex-1">
          <label className="form-label">Last name *</label>
          <input className={`form-input ${errors.lastName ? 'error' : ''}`} value={data.lastName || ''} onChange={(e) => onChange('lastName', e.target.value)} placeholder="Smith" />
          {errors.lastName && <div className="form-error">{errors.lastName}</div>}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Email address *</label>
<input type="email" className={`form-input ${errors.email ? 'error' : ''}`} value={data.email || ''} onChange={(e) => onChange('email', e.target.value)} placeholder="you@example.com" autoComplete="new-email" />
        {errors.email && <div className="form-error">{errors.email}</div>}
      </div>
      <div className="form-group">
        <label className="form-label">Mobile number *</label>
<input type="tel" className={`form-input ${errors.phone ? 'error' : ''}`} value={data.phone || ''} onChange={(e) => onChange('phone', e.target.value)} placeholder="04XX XXX XXX" autoComplete="new-phone" />
        {errors.phone && <div className="form-error">{errors.phone}</div>}
      </div>
      <div className="form-group">
        <label className="form-label">Date of birth *</label>
        <input type="date" className={`form-input ${errors.dob ? 'error' : ''}`} value={data.dob || ''} onChange={(e) => onChange('dob', e.target.value)} />
        {errors.dob && <div className="form-error">{errors.dob}</div>}
      </div>
      <div className="form-group">
        <label className="form-label">Password *</label>
        <div className="relative">
<input type={showPw ? 'text' : 'password'} className={`form-input ${errors.password ? 'error' : ''}`} value={data.password || ''} onChange={(e) => onChange('password', e.target.value)} placeholder="Min 8 characters" style={{ paddingRight: 44 }} autoComplete="new-password" />

          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-none border-0 cursor-pointer text-[1.1rem] p-1" tabIndex={-1}>
            {showPw ? '🙈' : '👁️'}
          </button>
        </div>
        {errors.password && <div className="form-error">{errors.password}</div>}
        {!errors.password && <div className="form-hint">Include uppercase, lowercase, and a number.</div>}
      </div>
    </div>
  );
}

// ═══════════════ STEP 2: IDENTITY ═══════════════
function StepIdentity({ data, onChange, onUpload, errors }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Identity Verification</h2>
      <p className="text-body mb-8 leading-relaxed">Upload your driver's licence (front and back).</p>
      <FileUploadField label="Driver's Licence — Front *" field="licenceFront" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.licenceFront} />
      <FileUploadField label="Driver's Licence — Back *" field="licenceBack" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.licenceBack} />
      <div className="form-group">
        <label className="form-label">Licence number * (8-10 characters)</label>
        <input className={`form-input ${errors.licenceNumber ? 'error' : ''}`} value={data.licenceNumber || ''} onChange={(e) => onChange('licenceNumber', e.target.value)} maxLength={10} />
        {errors.licenceNumber && <div className="form-error">{errors.licenceNumber}</div>}
      </div>
      <div className="form-group">
        <label className="form-label">Licence expiry date *</label>
        <input type="date" className={`form-input ${errors.licenceExpiry ? 'error' : ''}`} value={data.licenceExpiry || ''} onChange={(e) => onChange('licenceExpiry', e.target.value)} min={today()} />
        {errors.licenceExpiry && <div className="form-error">{errors.licenceExpiry}</div>}
      </div>
      <FileUploadField label="Proof of Identity (Passport or Medicare) *" field="proofOfIdentity" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.proofOfIdentity} />

      {/* ─── National Police Check (NSW point-to-point: BSP must obtain & retain it) ───
          TODO (pre-launch): replace this manual upload with a verified check from an
          ACIC-accredited provider (e.g. Fit2Work / CVCheck) so the result comes from the
          source and cannot be altered. See driverService + a screeningService integration. */}
      <div className="p-3 rounded-xl bg-slate-50 mt-2 mb-4 text-[0.8rem] text-body leading-relaxed">
        <strong>National Police Check.</strong> NSW law requires Reservo to hold a current
        National Police Check for every driver. Get one from the NSW Police or an online
        provider, then upload it below. It must have been issued within the last 3 months.
      </div>
      <FileUploadField label="National Police Check *" field="policeCheck" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.policeCheck} />
      <div className="form-group">
        <label className="form-label">Police check issue date *</label>
        <input type="date" className={`form-input ${errors.policeCheckIssueDate ? 'error' : ''}`} value={data.policeCheckIssueDate || ''} onChange={(e) => onChange('policeCheckIssueDate', e.target.value)} max={today()} />
        {errors.policeCheckIssueDate && <div className="form-error">{errors.policeCheckIssueDate}</div>}
      </div>
      <div className="form-group">
        <label className="form-label">Police check reference number (optional)</label>
        <input className="form-input" value={data.policeCheckReference || ''} onChange={(e) => onChange('policeCheckReference', e.target.value)} maxLength={40} />
      </div>
    </div>
  );
}

// ═══════════════ STEP 3: PT CODE (NSW Point to Point) ═══════════════
function StepDriverAuthority({ data, onChange, onUpload, errors }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">NSW Point to Point — PT Code</h2>
      <p className="text-body mb-8 leading-relaxed">
        To drive point-to-point (rideshare/hire) in NSW, the PT Code (Passenger Transport
        Licence Code) must be applied to your NSW driver licence. You apply for this through
        Service NSW — it is added to your existing licence, not issued as a separate number.
        Reservo verifies your PT Code via the NSW Driver Vehicle Dashboard before activating
        your account.
      </p>
      <FileUploadField label="NSW Driver Licence — copy showing PT Code *" field="driverAuthority" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.driverAuthority} />
      <div className="form-group">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.ptCodeAttested}
            onChange={(e) => onChange('ptCodeAttested', e.target.checked)}
            className="mt-1"
          />
          <span className="text-body text-[0.92rem] leading-relaxed">
            I confirm the PT Code (Passenger Transport Licence Code) is currently applied to
            my NSW driver licence, and I understand Reservo will verify this with the NSW
            Point to Point Transport Commissioner before my account is activated. *
          </span>
        </label>
        {errors.ptCodeAttested && <div className="form-error">{errors.ptCodeAttested}</div>}
      </div>
      <div className="form-hint">
        Your licence number and expiry were captured in the previous step. The PT Code is tied
        to that licence, so it shares the same expiry.
      </div>
    </div>
  );
}

// ═══════════════ STEP 4: WWCC ═══════════════
function StepWWCC({ data, onChange, onUpload, errors }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Working With Children Check</h2>
      <p className="text-body mb-8 leading-relaxed">Required for transporting passengers under 18. If you don't have a WWCC, you won't be matched with minor passengers.</p>
      <div className="form-group">
        <label className="form-label">Do you have a valid WWCC? *</label>
        <select className={`form-input form-select ${errors.hasWWCC ? 'error' : ''}`} value={data.hasWWCC || ''} onChange={(e) => onChange('hasWWCC', e.target.value)}>
          <option value="">Select...</option>
          <option value="yes">Yes</option>
          <option value="no">No — I'll skip minor transport</option>
        </select>
        {errors.hasWWCC && <div className="form-error">{errors.hasWWCC}</div>}
      </div>
      {data.hasWWCC === 'yes' && (
        <>
          <FileUploadField label="WWCC Document *" field="wwccDocument" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.wwccDocument} />
          <div className="form-group">
            <label className="form-label">WWCC number *</label>
            <input className={`form-input ${errors.wwccNumber ? 'error' : ''}`} value={data.wwccNumber || ''} onChange={(e) => onChange('wwccNumber', e.target.value)} />
            {errors.wwccNumber && <div className="form-error">{errors.wwccNumber}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Expiry date *</label>
            <input type="date" className={`form-input ${errors.wwccExpiry ? 'error' : ''}`} value={data.wwccExpiry || ''} onChange={(e) => onChange('wwccExpiry', e.target.value)} min={today()} />
            {errors.wwccExpiry && <div className="form-error">{errors.wwccExpiry}</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════ STEP 5: ABN ═══════════════
function StepABN({ data, onChange, errors }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">ABN &amp; GST Registration</h2>
      <p className="text-body mb-8 leading-relaxed">All drivers must have an ABN with active GST registration. Admin will verify your ABN during the review process.</p>
      <div className="form-group">
        <label className="form-label">ABN * (11 digits)</label>
        <input className={`form-input ${errors.abn ? 'error' : ''}`} value={data.abn || ''} onChange={(e) => onChange('abn', e.target.value.replace(/[^\d\s]/g, ''))} placeholder="XX XXX XXX XXX" maxLength={14} />
        {errors.abn && <div className="form-error">{errors.abn}</div>}
      </div>
    </div>
  );
}

// ═══════════════ STEP 6: VEHICLE ═══════════════
function StepVehicle({ data, onChange, errors }) {
  const COLOURS = ['White','Black','Silver','Grey','Red','Blue','Dark Blue','Green','Bronze','Gold','Burgundy','Brown','Beige','Orange','Yellow','Other'];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 11 }, (_, i) => currentYear + 1 - i);
  const models = data.make ? (VEHICLE_MAKES[data.make] || []) : [];

  // Auto-classify tier when make/model changes
  const handleMakeChange = (make) => {
    onChange('make', make);
    onChange('model', '');
    onChange('tier', '');
  };
  const handleModelChange = (model) => {
    onChange('model', model);
    const tier = classifyVehicleTier(data.make, model);
    onChange('tier', tier || '');
  };

  const tierInfo = data.tier ? TIER_INFO[data.tier] : null;

  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Vehicle Details</h2>
      <p className="text-body mb-8 leading-relaxed">Register the vehicle you'll use for Reservo trips. Your service tier is automatically assigned based on your vehicle.</p>
      <div className="flex gap-4">
        <div className="form-group flex-1">
          <label className="form-label">Make *</label>
          <select className={`form-input form-select ${errors.make ? 'error' : ''}`} value={data.make || ''} onChange={(e) => handleMakeChange(e.target.value)}>
            <option value="">Select make...</option>
            {Object.keys(VEHICLE_MAKES).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {errors.make && <div className="form-error">{errors.make}</div>}
        </div>
        <div className="form-group flex-1">
          <label className="form-label">Model *</label>
          <select className={`form-input form-select ${errors.model ? 'error' : ''}`} value={data.model || ''} onChange={(e) => handleModelChange(e.target.value)} disabled={!data.make}>
            <option value="">{data.make ? 'Select model...' : 'Select make first'}</option>
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          {errors.model && <div className="form-error">{errors.model}</div>}
        </div>
      </div>

      {/* Auto-assigned tier display */}
      {tierInfo && (
        <div className="p-4 rounded-2xl mb-5" style={{ border: `2px solid ${tierInfo.colour}`, background: `${tierInfo.colour}10` }}>
          <div className="flex items-center gap-2.5">
            <div className="w-3 h-3 rounded-full" style={{ background: tierInfo.colour }} />
            <span className="font-bold text-brand-dark text-[1rem]">{tierInfo.name}</span>
          </div>
          <div className="text-[0.85rem] text-body mt-1 pl-[22px]">{tierInfo.desc}</div>
          <div className="text-[0.8rem] text-muted mt-0.5 pl-[22px]">Max {tierInfo.maxPax} passengers</div>
        </div>
      )}

      <div className="flex gap-4">
        <div className="form-group flex-1">
          <label className="form-label">Year *</label>
          <select className={`form-input form-select ${errors.year ? 'error' : ''}`} value={data.year || ''} onChange={(e) => onChange('year', e.target.value)}>
            <option value="">Select year...</option>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {errors.year && <div className="form-error">{errors.year}</div>}
        </div>
        <div className="form-group flex-1">
          <label className="form-label">Colour *</label>
          <select className={`form-input form-select ${errors.colour ? 'error' : ''}`} value={data.colour || ''} onChange={(e) => onChange('colour', e.target.value)}>
            <option value="">Select colour...</option>
            {COLOURS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {errors.colour && <div className="form-error">{errors.colour}</div>}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Registration plate *</label>
        <input className={`form-input ${errors.plate ? 'error' : ''}`} value={data.plate || ''} onChange={(e) => onChange('plate', e.target.value.toUpperCase())} placeholder="ABC 123" />
        {errors.plate && <div className="form-error">{errors.plate}</div>}
      </div>
      <div className="form-group">
<label className="form-label">Maximum passengers * (excluding driver)</label>
        <select className={`form-input form-select ${errors.maxPassengers ? 'error' : ''}`} value={data.maxPassengers || ''} onChange={(e) => onChange('maxPassengers', e.target.value)}>
          <option value="">Select...</option>
{[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
{errors.maxPassengers && <div className="form-error">{errors.maxPassengers}</div>}
<div className="form-hint">How many passengers can ride comfortably, not counting the driver.</div>
        
      </div>
      <div className="form-group">
        <label className="form-label">Child seat equipped?</label>
        <select className="form-input form-select" value={data.childSeat || ''} onChange={(e) => onChange('childSeat', e.target.value)}>
          <option value="">Select...</option>
          <option value="none">No child seat</option>
          <option value="rear-facing">Rear-facing (0–6 months)</option>
          <option value="forward-facing">Forward-facing (6mo–4yrs)</option>
          <option value="booster">Booster seat (4–7 years)</option>
          <option value="multiple">Multiple seats available</option>
        </select>
      </div>
    </div>
  );
}
// ═══════════════ STEP 7: INSURANCE ═══════════════
function StepInsurance({ data, onChange, onUpload, errors }) {
  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Insurance &amp; Registration</h2>
      <p className="text-body mb-8 leading-relaxed">Upload your vehicle's insurance and registration documents.</p>
      <FileUploadField label="CTP Insurance (Green Slip) *" field="ctpInsurance" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.ctpInsurance} />
      <div className="form-group">
        <label className="form-label">CTP expiry date *</label>
        <input type="date" className={`form-input ${errors.ctpExpiry ? 'error' : ''}`} value={data.ctpExpiry || ''} onChange={(e) => onChange('ctpExpiry', e.target.value)} min={today()} />
        {errors.ctpExpiry && <div className="form-error">{errors.ctpExpiry}</div>}
      </div>
      <FileUploadField label="Comprehensive Insurance *" field="comprehensiveInsurance" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.comprehensiveInsurance} />
      <div className="form-group">
        <label className="form-label">Comprehensive insurance expiry *</label>
        <input type="date" className={`form-input ${errors.compExpiry ? 'error' : ''}`} value={data.compExpiry || ''} onChange={(e) => onChange('compExpiry', e.target.value)} min={today()} />
        {errors.compExpiry && <div className="form-error">{errors.compExpiry}</div>}
      </div>
      <FileUploadField label="Vehicle Registration Certificate *" field="vehicleRego" data={data} onUpload={onUpload} accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf" error={errors.vehicleRego} />
      <div className="form-group">
        <label className="form-label">Registration expiry *</label>
        <input type="date" className={`form-input ${errors.regoExpiry ? 'error' : ''}`} value={data.regoExpiry || ''} onChange={(e) => onChange('regoExpiry', e.target.value)} min={today()} />
        {errors.regoExpiry && <div className="form-error">{errors.regoExpiry}</div>}
      </div>
    </div>
  );
}

// ═══════════════ STEP 8: SERVICE PREFERENCES ═══════════════
function StepServicePreferences({ data, onChange, errors }) {
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const selected = data.preferredSuburbs ? data.preferredSuburbs.split(',').map(s => s.trim()).filter(Boolean) : [];

  const filtered = search
    ? NSW_SUBURBS.filter(s => s.toLowerCase().includes(search.toLowerCase()))
    : NSW_SUBURBS;

  const toggleSuburb = (suburb) => {
    const updated = selected.includes(suburb)
      ? selected.filter(s => s !== suburb)
      : [...selected, suburb];
    onChange('preferredSuburbs', updated.join(', '));
  };

  const toggleRegion = (suburbs, add) => {
    let updated;
    if (add) {
      const toAdd = suburbs.filter(s => !selected.includes(s));
      updated = [...selected, ...toAdd];
    } else {
      updated = selected.filter(s => !suburbs.includes(s));
    }
    onChange('preferredSuburbs', updated.join(', '));
  };

  const selectAll = () => onChange('preferredSuburbs', NSW_SUBURBS.join(', '));
  const clearAll = () => onChange('preferredSuburbs', '');

  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Service Preferences</h2>
      <p className="text-body mb-8 leading-relaxed">Set your preferred service areas and referral code.</p>
      <div className="form-group">
        <label className="form-label">Preferred service suburbs * (select at least 1)</label>

        {/* Selected count + actions */}
        <div className="flex justify-between items-center mb-2">
          <span className="text-[0.85rem] text-body font-semibold">
            {selected.length} suburb{selected.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={selectAll} className="bg-none border-0 cursor-pointer text-brand-primary font-semibold text-[0.8rem] p-0">Select all</button>
            <button type="button" onClick={clearAll} className="bg-none border-0 cursor-pointer text-brand-primary font-semibold text-[0.8rem] p-0">Clear all</button>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex gap-1 mb-3 rounded-[10px] p-[3px]" style={{ background: '#f1f5f9' }}>
          <button type="button" onClick={() => setViewMode('map')} className="flex-1 px-3 py-2 rounded-lg border-0 cursor-pointer font-semibold text-[0.85rem]"
            style={{
              background: viewMode === 'map' ? '#fff' : 'transparent',
              color: viewMode === 'map' ? '#0a1628' : '#94a3b8',
              boxShadow: viewMode === 'map' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>🗺️ Map View</button>
          <button type="button" onClick={() => setViewMode('list')} className="flex-1 px-3 py-2 rounded-lg border-0 cursor-pointer font-semibold text-[0.85rem]"
            style={{
              background: viewMode === 'list' ? '#fff' : 'transparent',
              color: viewMode === 'list' ? '#0a1628' : '#94a3b8',
              boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}>📋 List View</button>
        </div>

        {/* Selected tags */}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3 max-h-20 overflow-y-auto p-1">
            {selected.slice(0, 20).map(s => (
              <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-[0.85rem]" style={{ background: 'rgba(45,212,168,0.08)', color: '#1a9e7a' }}>
                {s}
                <button type="button" onClick={() => toggleSuburb(s)} className="bg-none border-0 cursor-pointer font-bold text-[1rem] p-0 leading-none" style={{ color: '#ff6b6b' }}>×</button>
              </span>
            ))}
            {selected.length > 20 && <span className="text-[0.8rem] text-muted px-2 py-1.5">+{selected.length - 20} more</span>}
          </div>
        )}

        {errors.preferredSuburbs && <div className="form-error mb-2">{errors.preferredSuburbs}</div>}

        {/* MAP VIEW */}
        {viewMode === 'map' && (
          <NSWRegionMap selected={selected} onToggleRegion={toggleRegion} onToggleSuburb={toggleSuburb} />
        )}

        {/* LIST VIEW */}
        {viewMode === 'list' && (
          <>
            <input className="form-input mb-2" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter suburbs..." />
            <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-xl bg-white">
              {filtered.map(s => (
                <label key={s} onClick={() => toggleSuburb(s)} className="flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer"
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: selected.includes(s) ? 'rgba(45,212,168,0.06)' : 'transparent',
                  }}>
                  <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                    style={{
                      border: `2px solid ${selected.includes(s) ? '#2dd4a8' : '#cbd5e1'}`,
                      background: selected.includes(s) ? '#2dd4a8' : 'transparent',
                    }}>
                    {selected.includes(s) && <span className="text-white text-[0.7rem] font-bold">✓</span>}
                  </div>
                  <span className="text-[0.9rem] text-brand-dark">{s}</span>
                </label>
              ))}
              {filtered.length === 0 && <div className="p-4 text-center text-muted">No suburbs match "{search}"</div>}
            </div>
          </>
        )}

        <div className="form-hint mt-2">You'll be prioritised for bookings in these areas.</div>
      </div>
      <div className="form-group">
        <label className="form-label">Maximum pickup distance (km)</label>
        <select className="form-input form-select" value={data.maxPickupDistance || '20'} onChange={(e) => onChange('maxPickupDistance', e.target.value)}>
          <option value="10">10 km</option>
          <option value="20">20 km</option>
          <option value="30">30 km</option>
          <option value="50">50 km</option>
          <option value="any">Any distance</option>
        </select>
      </div>
<div className="form-group">
        <label className="form-label">Referral code (optional)</label>
        <input className="form-input" value={data.referralCode || ''} onChange={(e) => onChange('referralCode', e.target.value)} placeholder="D014-R4K7" />
        <div className="form-hint">If another driver referred you, enter their code. You'll both receive $200 after 50 completed trips.</div>
      </div>
    </div>
  );
}

// ═══════════════ STEP 9: CONSENTS & AUTHORISATIONS (Batch 10) ═══════════════
// [VERIFY: NSW Point to Point Commissioner — confirm exact wording of each consent statement]
// Wording version: v1.0-2026-01-01. When wording changes, increment the version string here AND
// in CONSENT_WORDING_VERSION below, so the audit trail captures which version each driver agreed to.
const CONSENT_WORDING_VERSION = 'v1.0-2026-01-01';

function StepConsents({ data, onChange, errors }) {
  const consents = [
    {
      key: 'driving_record',
      label: 'Driving Record Check',
      mandatory: true,
      wording: 'I authorise Reservo to verify my driving record with Transport for NSW and the NSW Point to Point Transport Commissioner, and to obtain my current driving history at any time during my engagement.',
    },
    {
      key: 'criminal_history',
      label: 'National Criminal History Check',
      mandatory: true,
      wording: 'I authorise Reservo to conduct a National Criminal History Check on me, and to refresh that check periodically during my engagement.',
    },
    {
      key: 'wwcc',
      label: 'Working With Children Check',
      mandatory: false,
      wording: 'If I will be transporting passengers under 18, I authorise Reservo to verify my Working With Children Check (WWCC) with the NSW Office of the Children\'s Guardian.',
    },
    {
      key: 'medical_fitness',
      label: 'Medical Fitness Declaration',
      mandatory: true,
      wording: 'I declare that I am medically fit to drive commercially under NSW Roads and Maritime Services standards, and will notify Reservo immediately if my medical status changes.',
    },
    {
      key: 'data_sharing_pt',
      label: 'Data Sharing with NSW Point to Point Transport Commissioner',
      mandatory: true,
      wording: 'I consent to Reservo sharing my trip records, incident reports, complaints, vehicle inspection records, and authorisation status with the NSW Point to Point Transport Commissioner as required by law.',
    },
    {
      key: 'dvd_consent',
      label: 'Driver Vehicle Dashboard (DVD) Data Consent',
      mandatory: true,
      wording: 'I consent to Reservo collecting, storing, and electronically reporting Driver Vehicle Dashboard (DVD) data — including but not limited to vehicle inspections, in-service compliance checks, and trip telemetry — to the NSW Point to Point Transport Commissioner.',
    },
  ];

  return (
    <div className="animate-fade-up">
      <h2 className="font-serif text-[1.5rem] text-brand-dark mb-2">Consents and Authorisations</h2>
      <p className="text-body mb-8 leading-relaxed">
        NSW Point to Point Transport regulations require us to record your explicit consent to certain checks
        and data sharing. Please read each statement and tick the box to consent. Mandatory consents are required
        to complete your application.
      </p>

      <div className="p-3 rounded-xl mb-5 text-[0.82rem] leading-relaxed" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
        ⚠ <strong>Your consent is recorded permanently</strong> along with the wording version, your IP address,
        and a timestamp. Reservo retains these records for at least 7 years.
      </div>

      {consents.map((c) => {
        const key = `consent_${c.key}`;
        const checked = !!data[key];
        const errored = errors[key];
        return (
          <div key={c.key} className="p-3.5 rounded-xl mb-3" style={{
            border: `1.5px solid ${errored ? '#ff6b6b' : checked ? '#2dd4a8' : '#e2e8f0'}`,
            background: checked ? 'rgba(45,212,168,0.04)' : '#fff',
          }}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(key, e.target.checked)}
                className="mt-[3px] flex-shrink-0"
              />
              <div className="flex-1">
                <div className="font-bold text-brand-dark mb-1 text-[0.92rem]">
                  {c.label}
                  {c.mandatory && <span className="ml-1" style={{ color: '#dc2626' }}>*</span>}
                  {!c.mandatory && <span className="ml-1.5 text-[0.78rem] font-medium text-muted">(optional)</span>}
                </div>
                <div className="text-[0.85rem] text-body leading-relaxed">
                  {c.wording}
                </div>
                {errored && <div className="form-error mt-1.5">{errored}</div>}
              </div>
            </label>
          </div>
        );
      })}

      <div className="p-3 rounded-xl bg-slate-50 mt-4 text-[0.78rem] text-body leading-relaxed">
        By submitting this application, you confirm all information provided is true and accurate.
        You agree to Reservo's <a href="/legal" target="_blank" rel="noopener noreferrer" className="text-brand-primary font-semibold">Driver Agreement, Privacy Policy, and Code of Conduct</a>.
      </div>
    </div>
  );
}

// ═══════════════ SHARED FILE UPLOAD COMPONENT ═══════════════

function FileUploadField({ label, field, data, onUpload, accept, error }) {
  const uploaded = data[`${field}_uploaded`];
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {uploaded ? (
        <div className="px-4 py-3 rounded-xl font-medium text-[0.9rem]" style={{ background: 'rgba(45,212,168,0.06)', border: '1.5px solid rgba(45,212,168,0.2)', color: '#1a9e7a' }}>✓ {uploaded.fileName || 'Uploaded'}</div>
      ) : (
        <input type="file" accept={accept} className={`form-input ${error ? 'error' : ''}`} style={{ padding: '10px 16px' }}
          onChange={(e) => e.target.files[0] && onUpload(e.target.files[0], field)} />
      )}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

// ═══════════════ STEP VALIDATION ═══════════════
function validateStep(step, data, abnResult) {
  const errs = {};

  switch (step) {
    case 1: // Personal Details
      if (!data.firstName?.trim()) errs.firstName = 'First name is required';
      else if (!/^[a-zA-Z\s'-]+$/.test(data.firstName.trim())) errs.firstName = 'Letters only';
      if (!data.lastName?.trim()) errs.lastName = 'Last name is required';
      else if (!/^[a-zA-Z\s'-]+$/.test(data.lastName.trim())) errs.lastName = 'Letters only';
      if (!data.email?.trim()) errs.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = 'Invalid email';
      if (!data.phone?.trim()) errs.phone = 'Phone is required';
      else if (!/^(\+61|04)\d{8,9}$/.test(data.phone.replace(/\s/g, ''))) errs.phone = 'Enter a valid AU mobile number';
      if (!data.dob) errs.dob = 'Date of birth is required';
      else {
        const age = Math.floor((Date.now() - new Date(data.dob).getTime()) / (365.25 * 86400000));
        if (age < 18) errs.dob = 'Must be at least 18 years old';
        if (age > 80) errs.dob = 'Please check your date of birth';
      }
      if (!data.password) errs.password = 'Password is required';
      else if (data.password.length < 8) errs.password = 'Minimum 8 characters';
      else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/.test(data.password)) errs.password = 'Include uppercase, lowercase, and a number';
      break;

    case 2: // Identity
      if (!data.licenceFront_uploaded) errs.licenceFront = 'Upload licence front';
      if (!data.licenceBack_uploaded) errs.licenceBack = 'Upload licence back';
      if (!data.licenceNumber?.trim()) errs.licenceNumber = 'Licence number is required';
      else if (data.licenceNumber.trim().length < 8 || data.licenceNumber.trim().length > 10) errs.licenceNumber = 'Licence number must be 8-10 characters';
      if (!data.licenceExpiry) errs.licenceExpiry = 'Expiry date is required';
      else if (isExpired(data.licenceExpiry)) errs.licenceExpiry = 'Licence has expired — cannot register with an expired licence';
      else if (isDateTooSoon(data.licenceExpiry, 30)) errs.licenceExpiry = 'Licence expires within 30 days — please renew first';
      if (!data.proofOfIdentity_uploaded) errs.proofOfIdentity = 'Upload proof of identity';
      if (!data.policeCheck_uploaded) errs.policeCheck = 'Upload your National Police Check';
      if (!data.policeCheckIssueDate) errs.policeCheckIssueDate = 'Issue date is required';
      else if (new Date(data.policeCheckIssueDate) > new Date()) errs.policeCheckIssueDate = 'Issue date cannot be in the future';
      else if (isDateTooOld(data.policeCheckIssueDate, 90)) errs.policeCheckIssueDate = 'Police check must have been issued within the last 3 months';
      break;

    case 3: // PT Code (NSW Point to Point)
      if (!data.driverAuthority_uploaded) errs.driverAuthority = 'Upload a copy of your NSW driver licence showing the PT Code';
      if (!data.ptCodeAttested) errs.ptCodeAttested = 'You must confirm the PT Code is applied to your licence';
      break;


    case 4: // WWCC
      if (!data.hasWWCC) errs.hasWWCC = 'Please select an option';
      if (data.hasWWCC === 'yes') {
        if (!data.wwccDocument_uploaded) errs.wwccDocument = 'Upload WWCC document';
        if (!data.wwccNumber?.trim()) errs.wwccNumber = 'WWCC number is required';
        if (!data.wwccExpiry) errs.wwccExpiry = 'Expiry date is required';
        else if (isExpired(data.wwccExpiry)) errs.wwccExpiry = 'WWCC has expired';
      }
      break;

    case 5: // ABN
      if (!data.abn?.trim()) errs.abn = 'ABN is required';
      else {
        const digits = data.abn.replace(/\s/g, '');
        if (!/^\d{11}$/.test(digits)) errs.abn = 'ABN must be exactly 11 digits';
        
      }
      break;

    case 6: // Vehicle
      if (!data.make?.trim()) errs.make = 'Make is required';
      if (!data.model?.trim()) errs.model = 'Model is required';
      if (!data.year) errs.year = 'Year is required';
else if (parseInt(data.year) < new Date().getFullYear() - 9 || parseInt(data.year) > new Date().getFullYear() + 1) errs.year = 'Vehicle must be less than 10 years old';
      if (!data.colour?.trim()) errs.colour = 'Colour is required';
if (!data.plate?.trim()) errs.plate = 'Registration plate is required';
      else {
        const plate = data.plate.trim().replace(/\s/g, '');
        if (plate.length < 2 || plate.length > 7) errs.plate = 'Plate must be 2-7 characters';
        else if (!/^[A-Z0-9]+$/.test(plate)) errs.plate = 'Letters and numbers only';
        else if (!/[A-Z]/.test(plate)) errs.plate = 'Must contain at least one letter';
        else if (!/[0-9]/.test(plate)) errs.plate = 'Must contain at least one number';
      }
      if (!data.tier) errs.tier = 'Select make and model to determine your tier';

      if (!data.maxPassengers) errs.maxPassengers = 'Maximum passengers is required';
else if (parseInt(data.maxPassengers) < 1 || parseInt(data.maxPassengers) > 24) errs.maxPassengers = '1-24 passengers';
      break;

    case 7: // Insurance
      if (!data.ctpInsurance_uploaded) errs.ctpInsurance = 'Upload CTP insurance';
      if (!data.ctpExpiry) errs.ctpExpiry = 'CTP expiry date is required';
      else if (isExpired(data.ctpExpiry)) errs.ctpExpiry = 'CTP insurance has expired';
      if (!data.comprehensiveInsurance_uploaded) errs.comprehensiveInsurance = 'Upload comprehensive insurance';
      if (!data.compExpiry) errs.compExpiry = 'Comprehensive expiry date is required';
      else if (isExpired(data.compExpiry)) errs.compExpiry = 'Comprehensive insurance has expired';
      if (!data.vehicleRego_uploaded) errs.vehicleRego = 'Upload registration certificate';
      if (!data.regoExpiry) errs.regoExpiry = 'Registration expiry date is required';
      else if (isExpired(data.regoExpiry)) errs.regoExpiry = 'Vehicle registration has expired';
      break;

    case 8: // Service Preferences
      const suburbs = data.preferredSuburbs?.split(',').map(s => s.trim()).filter(Boolean) || [];
      if (suburbs.length === 0) errs.preferredSuburbs = 'Select at least one suburb';
      break;

    case 9: // Consents (Batch 10)
      // Mandatory consents must be ticked
      if (!data.consent_driving_record) errs.consent_driving_record = 'Required to proceed';
      if (!data.consent_criminal_history) errs.consent_criminal_history = 'Required to proceed';
      if (!data.consent_medical_fitness) errs.consent_medical_fitness = 'Required to proceed';
      if (!data.consent_data_sharing_pt) errs.consent_data_sharing_pt = 'Required to proceed';
      if (!data.consent_dvd_consent) errs.consent_dvd_consent = 'Required to proceed';
      // WWCC consent is mandatory ONLY if hasWWCC was 'yes'
      if (data.hasWWCC === 'yes' && !data.consent_wwcc) errs.consent_wwcc = 'Required since you have a WWCC';
      break;

    default: break;
  }

  return errs;
}

// ═══════════════ MAIN WIZARD ═══════════════
export default function DriverRegistrationWizard() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const submittingRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [inviteValid, setInviteValid] = useState(null);
  const [abnResult, setAbnResult] = useState(null);
  const [verifyingABN, setVerifyingABN] = useState(false);
// Batch 10: total steps = original 8 + Consents step (9)
  // [SETUP REQUIRED: Add the 9th step to DRIVER_REG_STEPS in src/config/constants.js — see notes below]
  const totalSteps = Math.max(DRIVER_REG_STEPS.length, 9);

  // If a driver is already signed in (e.g. they navigated back here after submitting,
  // or refreshed post-submit), never re-run registration — send them to their status page.
  useEffect(() => {
    if (!submittingRef.current && currentUser && userProfile?.role === USER_ROLES.DRIVER) {
      navigate('/driver/application-status', { replace: true });
    }
  }, [currentUser, userProfile, navigate]);

  useEffect(() => {
    if (inviteId) {
      validateInvitationLink(inviteId)
        .then(() => setInviteValid(true))
        .catch((err) => { toast.error(err.message); setInviteValid(false); });
    }
  }, [inviteId]);

  const handleFieldChange = useCallback((field, value) => {
    setStepData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  }, [errors]);

  const handleFileUpload = useCallback(async (file, field) => {
    const check = validateUploadFile(file);
    if (!check.ok) {
      // Reject immediately at the step — do not store the file or mark it uploaded.
      setStepData((prev) => {
        const next = { ...prev };
        delete next[`${field}_file`];
        delete next[`${field}_uploaded`];
        return next;
      });
      setErrors((prev) => ({ ...prev, [field]: check.reason }));
      toast.error(check.reason);
      return;
    }
    setStepData((prev) => ({
      ...prev,
      [`${field}_file`]: file,
      [`${field}_uploaded`]: { fileName: file.name },
    }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    toast.success(`${file.name} selected`);
  }, [errors]);

  const handleVerifyABN = useCallback(async () => {
    if (!stepData.abn) return;
    const digits = stepData.abn.replace(/\s/g, '');
    if (!/^\d{11}$/.test(digits)) {
      toast.error('ABN must be exactly 11 digits');
      return;
    }
    setVerifyingABN(true);
    try {
      const result = await verifyABN(stepData.abn);
      setAbnResult(result);
      if (!result.valid) toast.error('ABN could not be verified.');
      else if (!result.gstRegistered) toast.error('ABN is valid but not GST-registered.');
      else toast.success('ABN verified and GST-registered!');
    } catch (err) {
      toast.error('ABN verification failed.');
    } finally {
      setVerifyingABN(false);
    }
  }, [stepData.abn]);

  const handleNext = async () => {
    const stepErrors = validateStep(currentStep, stepData, abnResult);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      toast.error('Please fix the errors before continuing');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
      setErrors({});
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    const stepErrors = validateStep(currentStep, stepData, abnResult);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;

    // Pre-flight: validate every selected document BEFORE creating the auth account, so a
    // bad file can never leave an orphaned account that blocks the retry with email-in-use.
    const fileErrs = {};
    let firstBadStep = null;
    for (const key of Object.keys(stepData)) {
      if (key.endsWith('_file') && stepData[key]) {
        const field = key.slice(0, -5);
        const chk = validateUploadFile(stepData[key]);
        if (!chk.ok) {
          fileErrs[field] = chk.reason;
          if (firstBadStep === null) firstBadStep = DOC_FIELD_STEP[field] || currentStep;
        }
      }
    }
    if (Object.keys(fileErrs).length > 0) {
      setErrors((prev) => ({ ...prev, ...fileErrs }));
      if (firstBadStep !== null) setCurrentStep(firstBadStep);
      toast.error('Some documents are an unsupported file type. Re-upload as JPEG, PNG, WebP, or PDF.');
      return;
    }

    // One account per phone — verify before creating the account so a duplicate can't orphan one.
    try {
      const phoneCheck = await checkPhoneAvailable(stepData.phone);
      if (phoneCheck && phoneCheck.available === false) {
        setErrors((prev) => ({ ...prev, phone: 'That mobile number is already registered to another account.' }));
        setCurrentStep(1);
        toast.error('That mobile number is already registered to another account.');
        return;
      }
    } catch (e) {
      // If the check itself fails (e.g. network), don't hard-block registration.
      console.warn('Phone availability check failed (continuing):', e.message);
    }

    setLoading(true);
    submittingRef.current = true;
    try {
const { user } = await createUserWithEmailAndPassword(auth, stepData.email, stepData.password);

// Give Firebase auth a moment to propagate
await new Promise(resolve => setTimeout(resolve, 500));

      await setDoc(doc(db, 'users', user.uid), {
        email: stepData.email.toLowerCase(),
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        phone: stepData.phone,
        dob: stepData.dob,
        role: USER_ROLES.DRIVER,
        status: USER_STATUS.PENDING,
        emailVerified: false,
        phoneVerified: false,
        hasWWCC: stepData.hasWWCC === 'yes',
        // NSW PT Code (Passenger Transport Licence Code) — applied to the driver's
        // licence via Service NSW; no standalone number exists. We record the driver's
        // attestation and verify via the Driver Vehicle Dashboard (DVD) at admin review.
        // The licence number/expiry captured in Step 2 are the authoritative credential.
        licenceNumber: stepData.licenceNumber || null,
        licenceExpiry: stepData.licenceExpiry || null,
        // National Police Check (NSW BSP obligation). Manual upload for now; verify at admin
        // review. TODO (pre-launch): replace with an ACIC-accredited verified check.
        policeCheckIssueDate: stepData.policeCheckIssueDate || null,
        policeCheckReference: stepData.policeCheckReference || null,
        policeCheckStatus: 'pending_admin_verification',
        ptCodeAttested: !!stepData.ptCodeAttested,
        ptCodeAttestedAt: stepData.ptCodeAttested ? serverTimestamp() : null,
        ptVerificationStatus: 'pending_admin_verification',  // admin confirms via DVD before activation
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });


      const docFields = ['licenceFront', 'licenceBack', 'proofOfIdentity', 'policeCheck', 'driverAuthority',
        'wwccDocument', 'ctpInsurance', 'comprehensiveInsurance', 'vehicleRego'];
      for (const field of docFields) {
        if (stepData[`${field}_file`]) {
          await uploadDocument(user.uid, stepData[`${field}_file`], field);
        }
      }

      // Record consents (Batch 10) — each consent creates an append-only audit record
      // with wording version, timestamp, and (server-side) IP address.
      const consentsToRecord = [
        { key: 'driving_record', given: !!stepData.consent_driving_record },
        { key: 'criminal_history', given: !!stepData.consent_criminal_history },
        { key: 'wwcc', given: !!stepData.consent_wwcc },
        { key: 'medical_fitness', given: !!stepData.consent_medical_fitness },
        { key: 'data_sharing_pt', given: !!stepData.consent_data_sharing_pt },
        { key: 'dvd_consent', given: !!stepData.consent_dvd_consent },
      ];
      for (const c of consentsToRecord) {
        if (c.given) {
          try {
            await recordDriverConsent(c.key, CONSENT_WORDING_VERSION);
          } catch (consentErr) {
            // Non-fatal — log but continue. Admin will see missing consents in their review.
            console.error(`Consent recording failed for ${c.key}:`, consentErr);
          }
        }
      }

      await saveVehicleDetails(user.uid, {

        make: stepData.make, model: stepData.model, year: parseInt(stepData.year),
        colour: stepData.colour, plate: stepData.plate, tier: stepData.tier,
        maxPassengers: parseInt(stepData.maxPassengers), childSeat: stepData.childSeat || 'none',
      });

      // Strip File objects before saving to Firestore
      const cleanData = {};
      for (const [key, value] of Object.entries(stepData)) {
        if (!(value instanceof File) && !key.endsWith('_file')) {
          cleanData[key] = value;
        }
      }
      await saveApplicationProgress(user.uid, cleanData, totalSteps);

      if (inviteId) await markInvitationUsed(inviteId, user.uid);
      await submitApplication(user.uid);

      try {
        const stripe = await createStripeConnectAccount(user.uid);
        if (stripe.onboardingUrl) {
          toast.success('Application submitted! Completing payment setup...');
          window.location.href = stripe.onboardingUrl;
          return;
        }
      } catch (stripeErr) {
        console.error('Stripe Connect setup deferred:', stripeErr);
      }

      toast.success('Application submitted! We\'ll review it within 48 hours.');
      navigate('/driver/application-status');
    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setErrors({ email: 'An account with this email already exists' });
        setCurrentStep(1);
      } else {
        toast.error(err.message || 'Registration failed.');
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  if (inviteId && inviteValid === false) {
    return (
      <div className="min-h-screen px-6 py-8" style={{ background: '#faf8f5' }}><div className="max-w-[640px] mx-auto">
        <div className="text-center p-10">
          <div className="text-[3rem] mb-4">❌</div>
          <h2 className="font-serif mb-3">Invalid Invitation</h2>
          <p className="text-body mb-6">This invitation link is invalid or has expired.</p>
          <Link to="/login" className="btn btn-primary">Go to Login</Link>
        </div>
      </div></div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <StepPersonalDetails data={stepData} onChange={handleFieldChange} errors={errors} />;
      case 2: return <StepIdentity data={stepData} onChange={handleFieldChange} onUpload={handleFileUpload} errors={errors} />;
      case 3: return <StepDriverAuthority data={stepData} onChange={handleFieldChange} onUpload={handleFileUpload} errors={errors} />;
      case 4: return <StepWWCC data={stepData} onChange={handleFieldChange} onUpload={handleFileUpload} errors={errors} />;
      case 5: return <StepABN data={stepData} onChange={handleFieldChange} errors={errors} />;
      case 6: return <StepVehicle data={stepData} onChange={handleFieldChange} errors={errors} />;
      case 7: return <StepInsurance data={stepData} onChange={handleFieldChange} onUpload={handleFileUpload} errors={errors} />;
      case 8: return <StepServicePreferences data={stepData} onChange={handleFieldChange} errors={errors} />;
      case 9: return <StepConsents data={stepData} onChange={handleFieldChange} errors={errors} />;
      default: return null;
    }
  };


  return (
    <div className="min-h-screen px-6 py-8" style={{ background: '#faf8f5' }}>
      <div className="max-w-[640px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[10px] flex items-center justify-center font-extrabold text-[16px]" style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>R</div>
            <span className="font-serif text-[1.3rem] text-brand-dark">Reservo</span>
          </Link>
          <span className="badge badge-teal">Driver Application</span>
        </div>
        <div className="stepper">
          {DRIVER_REG_STEPS.map((step) => (
            <div key={step.id} className={`stepper-step ${currentStep > step.id ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`} />
          ))}
        </div>
        <div className="flex items-center gap-3 mb-8">
          <span className="px-3.5 py-1.5 rounded-full font-semibold text-[0.85rem]" style={{ background: 'rgba(45,212,168,0.08)', color: '#1a9e7a' }}>{DRIVER_REG_STEPS[currentStep - 1].icon} Step {currentStep} of {totalSteps}</span>
          <span className="text-muted text-[0.85rem]">{DRIVER_REG_STEPS[currentStep - 1].title}</span>
        </div>
        <div className="bg-white rounded-3xl p-8 mb-6" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>{renderStep()}</div>
        <div className="flex items-center gap-3">
          {currentStep > 1 && <button className="btn btn-secondary" onClick={handleBack} disabled={loading}>← Back</button>}
          <div className="flex-1" />
          {currentStep < totalSteps ? (
            <button className="btn btn-primary" onClick={handleNext} disabled={loading}>Continue →</button>
          ) : (
            <button className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={loading}>
              {loading ? <><span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> Submitting...</> : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}